import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';

import {
  IncentiveOffer,
  IncentiveOfferOperationStatus,
} from 'modules/incentivesMarket/types';

import { menuStatusOptions } from '../../../constants/incentiveOfferOperationStatus';
import getOfferReference from '../../../utils/getOfferReference';

type IncentiveOfferOperationDetailHeaderProps = {
  incentiveOffer: IncentiveOffer | null;
  operationReference: string;
  operationStatus: IncentiveOfferOperationStatus;
  formattedRequestDate: string;
  sellerName: string;
  isAdmin: boolean;
  societyId: string;
  societyName: string;
  onUpdateStatus: (
    status: IncentiveOfferOperationStatus, // eslint-disable-line no-unused-vars
    comment?: string // eslint-disable-line no-unused-vars
  ) => void;
};

const IncentiveOfferOperationDetailHeader: FC<
  IncentiveOfferOperationDetailHeaderProps
> = ({
  incentiveOffer,
  operationReference,
  operationStatus,
  formattedRequestDate,
  sellerName,
  isAdmin,
  societyId,
  societyName,
  onUpdateStatus,
}) => {
  const { t } = useTranslate();
  const location = useLocation();

  const statusForMenu: IncentiveOfferOperationStatus = operationStatus;
  const beneficiaryQuery = new URLSearchParams(location.search).get('beneficiaryId');
  const pq = beneficiaryQuery ? `?beneficiaryId=${beneficiaryQuery}` : '';

  const offerRef = useMemo(
    () => getOfferReference(incentiveOffer),
    [incentiveOffer]
  );

  const currentMenuStepIndex = useMemo(
    () => menuStatusOptions.findIndex(({ key }) => key === statusForMenu),
    [statusForMenu]
  );

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: societyName, to: `/sociedad/${societyId}` },
      {
        label: t('IncentivesMarket'),
        to: `/mercado-de-incentivos/${societyId}${pq}`,
      },
      {
        label: t('Offers'),
        to: `/ofertas-incentivos/${societyId}${pq}`,
      },
      {
        label: offerRef,
        to: `/detalle-oferta-incentivos/${societyId}/${incentiveOffer?._id}${pq}`,
      },
      { label: operationReference, isActive: true },
    ],
    [t, societyName, societyId, offerRef, incentiveOffer, operationReference, pq]
  );

  return (
    <>
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block-head page-header-subtitle">
        <div className="nk-block-between">
          <div className="nk-block-head-content">
            <h3 className="nk-block-title page-title">
              {incentiveOffer?.type === 'SELL'
                ? t('PurchaseOperation')
                : t('SaleOperation')}
              <span className="badge badge-outline badge-primary ml-2">
                {operationReference}
              </span>
            </h3>
            <div className="nk-block-des text-soft">
              <p>
                {t('StartedOn')} {formattedRequestDate}
                {sellerName && (
                  <>
                    {' '}
                    · {t('Counterparty')}: <strong>{sellerName}</strong>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="nk-block-head-content">
            <div className="toggle-wrap nk-block-tools-toggle">
              <div className="toggle-expand-content" data-content="pageMenu">
                <ul className="nk-block-tools g-3">
                  {/* <li>
                    <span className={`badge ${statusBadge.className}`}>
                      <em className={`icon ni ${statusBadge.icon} mr-1`} />
                      <span>{t(statusBadge.label)}</span>
                    </span>
                  </li> */}

                  {isAdmin &&
                    operationStatus !== IncentiveOfferOperationStatus.COMPLETED &&
                    operationStatus !== IncentiveOfferOperationStatus.CANCELLED &&
                    operationStatus !==
                      IncentiveOfferOperationStatus.REJECTED && (
                      <li>
                        {/* @ts-ignore */}
                        <OperationMenuDots
                          title={t('ManageOperation')}
                          variant="primary"
                          menuOptions={[
                            ...menuStatusOptions.map((status, optionIndex) => ({
                              icon: status.icon,
                              text: t(status.key),
                              action: () =>
                                onUpdateStatus(
                                  status.key as IncentiveOfferOperationStatus
                                ),
                              dropdown: true,
                              disabled:
                                currentMenuStepIndex === -1
                                  ? statusForMenu === status.key
                                  : optionIndex <= currentMenuStepIndex,
                            })),
                            {
                              icon: 'ni-cross-circle',
                              text: t('Rejected'),
                              action: () =>
                                onUpdateStatus(
                                  IncentiveOfferOperationStatus.REJECTED
                                ),
                              dropdown: true,
                              borderTop: true,
                              disabled:
                                statusForMenu ===
                                IncentiveOfferOperationStatus.REJECTED,
                            },
                            {
                              icon: 'ni-na',
                              text: t('Cancelled'),
                              action: () =>
                                onUpdateStatus(
                                  IncentiveOfferOperationStatus.CANCELLED
                                ),
                              dropdown: true,
                              disabled:
                                statusForMenu ===
                                IncentiveOfferOperationStatus.CANCELLED,
                            },
                          ]}
                        />
                      </li>
                    )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncentiveOfferOperationDetailHeader;
